import React from "react";
import PublicLayout from "../components/PublicLayout";

export default function PrivacyPage() {
  React.useEffect(() => {
    document.title = "Privacy Policy | Bark Buddies";
  }, []);
  const updated = "February 2026";
  return (
    <PublicLayout>
      <section className="mx-auto max-w-3xl px-5 md:px-8 pt-12 md:pt-16 pb-24" data-testid="privacy-page">
        <div className="text-[12px] font-bold uppercase tracking-widest text-bark-teal">Legal</div>
        <h1 className="mt-2 text-4xl sm:text-5xl font-black tracking-tight text-bark-ink">
          Privacy Policy
        </h1>
        <p className="mt-3 text-[13px] text-bark-muted">Last updated: {updated}</p>

        <div className="mt-8 bg-white rounded-[28px] shadow-bark-soft p-6 md:p-10 legal-prose">
          <p>
            This Privacy Policy explains how <strong>Bark Buddies</strong> ("we", "us", or "our")
            collects, uses, and shares information when you use the Bark Buddies mobile app,
            website, and related services (the "Services"). By using the Services you agree to
            this Privacy Policy.
          </p>

          <h2>1. Information we collect</h2>
          <h3>Account information</h3>
          <p>
            When you create an account we collect your name, email address, password (stored as a
            secure hash), and profile details you choose to add.
          </p>
          <h3>Dog profiles</h3>
          <p>
            You may add information about your dog(s) including name, breed, age, size, energy
            level, temperament, photos, and preferences. This information is displayed to other
            Bark Buddies users to help them find compatible playmates.
          </p>
          <h3>Location</h3>
          <p>
            With your permission we use approximate device location to show nearby matches,
            meetups, and dog parks. You can disable location access at any time in your device
            settings; some features (such as nearby matches and park search) will not work without
            it.
          </p>
          <h3>Messages and user content</h3>
          <p>
            We store messages sent between users after a buddy request is accepted, meetup details
            you create, and any other content you post. Messages may be reviewed if reported by a
            user or if required to enforce our Terms.
          </p>
          <h3>Beta signup data</h3>
          <p>
            If you sign up for the Bark Buddies beta on our website, we collect the information
            you submit in the signup form: name, email, phone (optional), city/state, platform,
            device model, OS version, dog ownership, dog size, feature interests, and your
            consent choices. We also record the date/time of your signup, your IP address, and
            your browser user-agent for security and quality purposes.
          </p>
          <h3>Support requests</h3>
          <p>
            If you contact support, we collect the contents of your message and any information
            you choose to include, along with your email address so we can respond.
          </p>
          <h3>Automatically collected</h3>
          <p>
            We collect basic device information, app version, crash logs, and usage analytics to
            keep the Services stable and improve them.
          </p>

          <h2>2. How we use information</h2>
          <ul>
            <li>To operate and improve Bark Buddies, including matching, messaging, meetups, and dog park discovery.</li>
            <li>To manage the beta program, contact selected testers, and gather feedback.</li>
            <li>To keep the community safe, investigate reports, and enforce our Terms.</li>
            <li>To send transactional and support emails you'd expect from an account.</li>
            <li>To comply with legal obligations.</li>
          </ul>

          <h2>3. Sharing</h2>
          <p>
            We do not sell your personal information. We share information only with service
            providers who help us run the Services (such as hosting and email delivery), with law
            enforcement when legally required, and with other Bark Buddies users as needed to
            provide the app (for example, your dog profile is visible to potential buddy matches).
          </p>

          <h2>4. Your choices</h2>
          <ul>
            <li>You can edit or delete your dog profiles at any time.</li>
            <li>You can block or report other users from inside the app.</li>
            <li>You can withdraw beta emails consent by replying "unsubscribe" or emailing support.</li>
            <li>You can turn off location permission in device settings.</li>
          </ul>

          <h2>5. Account deletion</h2>
          <p>
            You can request deletion of your Bark Buddies account and associated data at any time
            by emailing <a className="bark-link" href="mailto:support@barkbuddies.app">support@barkbuddies.app</a>{" "}
            or by using the in-app "Delete Account" option. When you delete your account, we
            remove your profile, dogs, matches, buddy requests, and messages. Some records may be
            retained for a limited period to comply with legal obligations, prevent fraud, or
            resolve disputes.
          </p>

          <h2>6. Security</h2>
          <p>
            We use industry-standard measures to protect your information. No online service is
            perfectly secure, so we encourage you to use a strong password and keep your device
            up to date.
          </p>

          <h2>7. Children</h2>
          <p>
            Bark Buddies is not intended for children under 13. If you believe a child has
            provided us with information, please contact us so we can remove it.
          </p>

          <h2>8. Changes</h2>
          <p>
            We may update this Privacy Policy from time to time. Material changes will be
            announced in the app or by email.
          </p>

          <h2>9. Contact</h2>
          <p>
            Questions about this Privacy Policy? Email us at{" "}
            <a className="bark-link" href="mailto:support@barkbuddies.app">support@barkbuddies.app</a>.
          </p>
        </div>
      </section>
    </PublicLayout>
  );
}
